import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const SocialProof = () => {
  const successMetrics = [
    {
      value: '500+',
      label: 'Organizations Transformed',
      icon: 'Building',
      color: 'text-blue-600'
    },
    {
      value: '85%',
      label: 'Average Cost Reduction',
      icon: 'TrendingDown',
      color: 'text-green-600'
    },
    {
      value: '6 months',
      label: 'Average ROI Timeline',
      icon: 'Clock',
      color: 'text-purple-600'
    },
    {
      value: '99.9%',
      label: 'Client Satisfaction Rate',
      icon: 'Star',
      color: 'text-yellow-600'
    }
  ];

  const clientTestimonials = [
    {
      quote: `AI Clean Core helped us break free from a $2M annual vendor lock-in. Their open-source approach gave us complete control over our AI infrastructure while reducing costs by 75%. The transformation was seamless and the results exceeded our expectations.`,
      author: 'Jennifer Walsh',
      role: 'Chief Technology Officer',
      company: 'FinanceFlow Corp',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=80&h=80&fit=crop&crop=face',
      metrics: {
        savings: '$1.5M annually',
        timeline: '4 months',
        improvement: '75% cost reduction'
      }
    },
    {
      quote: `The team's expertise in open-source AI is unmatched. They didn't just implement a solution—they built our internal capabilities so we could innovate independently. Our AI development velocity increased by 300% after the transformation.`,
      author: 'David Chen',
      role: 'VP of Engineering',
      company: 'TechInnovate Solutions',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face',
      metrics: {
        savings: '$800K annually',
        timeline: '3 months',
        improvement: '300% faster development'
      }
    },
    {
      quote: `Moving from proprietary AI services to an open-source stack seemed daunting, but AI Clean Core made it effortless. We now have full data sovereignty, custom AI models, and the flexibility to adapt quickly to market changes.`,
      author: 'Maria Rodriguez',
      role: 'Chief Data Officer',
      company: 'RetailTech Dynamics',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face',
      metrics: {
        savings: '$1.2M annually',
        timeline: '5 months',
        improvement: '90% data sovereignty'
      }
    }
  ];

  const industryLogos = [
    { name: 'TechFlow Industries', logo: 'https://via.placeholder.com/120x60/1a365d/ffffff?text=TechFlow' },
    { name: 'DataCore Systems', logo: 'https://via.placeholder.com/120x60/1a365d/ffffff?text=DataCore' },
    { name: 'FinanceFlow Corp', logo: 'https://via.placeholder.com/120x60/1a365d/ffffff?text=FinanceFlow' },
    { name: 'RetailTech Dynamics', logo: 'https://via.placeholder.com/120x60/1a365d/ffffff?text=RetailTech' },
    { name: 'HealthAI Solutions', logo: 'https://via.placeholder.com/120x60/1a365d/ffffff?text=HealthAI' },
    { name: 'ManufacturingAI', logo: 'https://via.placeholder.com/120x60/1a365d/ffffff?text=MfgAI' }
  ];

  const recentEngagements = [
    {
      company: 'Global Manufacturing Corp',
      industry: 'Manufacturing',
      challenge: 'Vendor lock-in with $3M annual AI spend',
      result: 'Achieved 80% cost reduction in 6 months',
      date: 'Completed January 2025'
    },
    {
      company: 'HealthTech Innovations',
      industry: 'Healthcare',
      challenge: 'Need for HIPAA-compliant AI infrastructure',
      result: 'Built secure, compliant open-source AI platform',
      date: 'Completed December 2024'
    },
    {
      company: 'Financial Services Leader',
      industry: 'Finance',
      challenge: 'Regulatory compliance with proprietary AI',
      result: 'Implemented transparent, auditable AI systems',
      date: 'Completed November 2024'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Metrics */}
        <div className="text-center mb-20">
          <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
            Proven Results Across Industries
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-12">
            Our track record speaks for itself. Here's what organizations achieve when they choose AI independence.
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {successMetrics?.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-executive flex items-center justify-center mx-auto mb-4">
                  <Icon name={metric?.icon} size={24} className={metric?.color} />
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-brand-primary mb-2">
                  {metric?.value}
                </div>
                <div className="text-text-secondary font-medium">
                  {metric?.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Client Testimonials */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-brand-primary mb-4">
              What Our Clients Say
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Real stories from organizations that have successfully transformed their AI infrastructure
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {clientTestimonials?.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-executive p-8">
                <div className="flex items-center mb-6">
                  <Image
                    src={testimonial?.avatar}
                    alt={testimonial?.author}
                    className="w-16 h-16 rounded-full mr-4"
                  />
                  <div>
                    <div className="font-semibold text-brand-primary">{testimonial?.author}</div>
                    <div className="text-sm text-text-secondary">{testimonial?.role}</div>
                    <div className="text-sm text-text-secondary">{testimonial?.company}</div>
                  </div>
                </div>

                <blockquote className="text-text-secondary mb-6 italic leading-relaxed">
                  "{testimonial?.quote}"
                </blockquote>

                <div className="grid grid-cols-1 gap-3 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Annual Savings:</span>
                    <span className="font-semibold text-green-600">{testimonial?.metrics?.savings}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Implementation:</span>
                    <span className="font-semibold text-blue-600">{testimonial?.metrics?.timeline}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Key Result:</span>
                    <span className="font-semibold text-purple-600">{testimonial?.metrics?.improvement}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trusted by Industry Leaders */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-brand-primary mb-4">
              Trusted by Industry Leaders
            </h3>
            <p className="text-text-secondary">
              Join hundreds of forward-thinking organizations that have chosen AI independence
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {industryLogos?.map((logo, index) => (
              <div key={index} className="flex items-center justify-center">
                <Image
                  src={logo?.logo}
                  alt={logo?.name}
                  className="h-12 w-auto opacity-60 hover:opacity-100 transition-opacity duration-200"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Recent Success Stories */}
        <div>
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-brand-primary mb-4">
              Recent Success Stories
            </h3>
            <p className="text-text-secondary">
              Latest transformations completed by our team
            </p>
          </div>

          <div className="space-y-6">
            {recentEngagements?.map((engagement, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-executive p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1 mb-4 lg:mb-0">
                    <div className="flex items-center space-x-4 mb-3">
                      <h4 className="text-lg font-semibold text-brand-primary">
                        {engagement?.company}
                      </h4>
                      <span className="px-3 py-1 bg-brand-primary/10 text-brand-primary text-sm rounded-full">
                        {engagement?.industry}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-text-secondary">Challenge: </span>
                        <span className="text-text-primary">{engagement?.challenge}</span>
                      </div>
                      <div>
                        <span className="text-text-secondary">Result: </span>
                        <span className="text-green-600 font-medium">{engagement?.result}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="Calendar" size={16} />
                    <span>{engagement?.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Join These Success Stories?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Don't let vendor lock-in hold your organization back. Start your AI independence journey today and join the growing community of organizations that control their AI destiny.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-brand-accent hover:bg-brand-accent/90 text-gray-900 font-semibold px-8 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center">
                <Icon name="Calendar" size={16} className="mr-2" />
                Schedule Your Consultation
              </button>
              <button className="border border-white text-white hover:bg-white hover:text-brand-primary font-semibold px-8 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center">
                <Icon name="Download" size={16} className="mr-2" />
                Download Success Stories
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;